/**
 * Typed wrapper around /internal/* fetch calls.
 *
 * Injectable `fetch` and `baseUrl` so component tests can swap them.
 * Every method throws `ApiError` on non-2xx; callers render nice
 * messages. The bearer token comes from `useSession().state.token`.
 */

/**
 * Shape of the JSON body the license-server returns on error.
 * `error` is always present; other fields are route-specific (e.g.
 * `retry_after_seconds` on rate-limit rejections, `detail` on
 * cross-region-unconfigured failures).
 */
export interface ApiErrorBody {
  error: string;
  retry_after_seconds?: number;
  detail?: string;
  message?: string;
  [k: string]: unknown;
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly body: ApiErrorBody | null;
  constructor(status: number, message: string, body: ApiErrorBody | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * The message a human should see for a failed call.
 *
 * `ApiError.message` is the generic "PUT /path failed: 400". Fastify's
 * validation detail — "body/default_heartbeat_interval_seconds must be >= 60"
 * — is in the response body, which is the part that actually tells the
 * operator what to change.
 */
export function apiErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    const m = (e.body as { message?: string } | null)?.message;
    if (typeof m === 'string' && m.length > 0) return m;
    return e.message;
  }
  return (e as Error)?.message ?? String(e);
}

export interface ApiClientOptions {
  baseUrl: string;
  getToken: () => string | null;
  fetch?: typeof fetch;
}

export interface DashboardSummary {
  total_customers: number;
  active_licenses: number;
  heartbeats_24h: number;
  unresolved_anomalies: number;
  /** Unresolved anomalies split by stored severity; absent on an older license server. */
  unresolved_anomalies_by_severity?: { info: number; warn: number; critical: number };
  /** Sprint D widgets. */
  trials_expiring_7d: number;
  conversions_30d: number;
  stale_installations: number;
}
export interface LicenseDetail {
  id: string; customer_id: string; company_name: string; primary_email: string;
  plan_tier: string; license_type: string; status: string;
  activation_count: number; max_activations: number;
  /** Full key for owner/admin; `****abcd` for support/readonly (see activation_key_redacted). */
  activation_key: string;
  /** true when the server withheld the key because the signed-in role may not view it. */
  activation_key_redacted?: boolean;
  /** null = plan default. See updateTiming(). */
  heartbeat_interval_seconds?: number | null;
  revoke_grace_seconds?: number | null;
  issued_at: string; expires_at: string | null;
  stripe_subscription_id: string | null; stripe_customer_id: string | null;
  residency_region: string; suspension_source: string | null;
  features_json: string[]; limits_json: Record<string, unknown>;
  override_base: number | null; override_overage: Record<string, number> | null;
  override_source: string | null;
  last_invoice_amount: number | null; last_invoice_at: string | null;
}
export interface AuditLogRow {
  id: string;
  actor_type: string;
  actor_id: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  ip: string | null;
  reason: string | null;
  metadata_json: Record<string, unknown> | null;
  created_at: string;
}
export interface InstallationRow {
  id: string;
  license_id: string;
  customer_id: string;
  company_name: string;
  license_type: string;
  plan_tier: string;
  /** Full key for owner/admin; masked tail for support/readonly. */
  activation_key: string;
  activation_key_redacted?: boolean;
  os_platform: string;
  os_distro: string | null;
  os_version: string | null;
  binary_version: string | null;
  status: string;
  activated_at: string;
  last_heartbeat_at: string | null;
  heartbeat_age_seconds: number | null;
  /**
   * VF-35: the freshest contact the server has seen (licence pull, registration refresh or heartbeat) and the
   * server's verdict on it. Computed by the license-server's engine-liveness rule so every page agrees.
   */
  last_contact_at?: string | null;
  contact_age_seconds?: number | null;
  liveness?: Liveness;
  /** Offline or never heard from (kept for older consumers; same rule as `liveness`). */
  stale: boolean;
}

/** Server-side verdict on live contact (license-server/src/services/engine-liveness.ts). */
export type Liveness = 'online' | 'degraded' | 'offline' | 'never';

/** Engines that exist but have no installation row (Free / never activated), by liveness. */
export interface EnginesWithoutInstallation {
  total: number; online: number; degraded: number; offline: number; never: number;
}
export interface CustomerRow {
  id: string; company_name: string; primary_email: string; status: string; created_at: string;
}
export interface LicenseRow {
  id: string; customer_id: string; plan_tier: string; license_type: string;
  status: string; activation_count: number; max_activations: number;
  issued_at: string; expires_at: string | null;
}

// ── V2 / locked-model — phase C.2 admin response types ──────────────────

export interface CapacitySnapshot {
  captured_at:         number | null;     // unix epoch seconds
  instances_count:     number;
  instances_systemd:   number;
  instances_docker:    number;
  instances_vloud_app: number;
  linux_users_count:   number;
  cpu_cores_available: number | null;
  memory_total_gb:     number | null;
  disk_used_gb:        number | null;
}

export interface LicenseCapacityResponse {
  snapshots: CapacitySnapshot[];
  latest: {
    captured_at:        number | null;
    instances_count:    number;
    linux_users_count:  number;
  } | null;
  aggregate: {
    instances_p95:    number;
    instances_max:    number;
    linux_users_p95:  number;
    linux_users_max:  number;
    sample_count:     number;
  };
}

export type LifecycleEventType =
  | 'activated'
  | 'heartbeat_ok'
  | 'secret_rotated'
  | 'plan_changed'
  | 'limits_changed'
  | 'tier_changed'
  | 'warning'
  | 'degraded'
  | 'suspended'
  | 'revoked'
  | 'expired'
  | 'restored'
  | 'heartbeat_failed';

export interface LifecycleEvent {
  id:          string | undefined;
  event_type:  LifecycleEventType;
  reason:      string | null;
  actor_type:  'system' | 'staff' | 'customer';
  actor_id:    string | null;
  metadata:    Record<string, unknown>;
  occurred_at: number | null;
}
export interface TicketSlaSummary {
  open_count: number;
  awaiting_staff_count: number;
  stale_awaiting_customer_count: number;
  first_response_p50_seconds: number | null;
  first_response_p90_seconds: number | null;
  resolution_p50_seconds: number | null;
  resolution_p90_seconds: number | null;
  sample_size: number;
}

export interface AnomalyRow {
  id: string; license_id: string | null; severity: string; code: string;
  message: string; action_required: boolean; detected_at: string; resolved_at: string | null;
}
export interface PlatformBugRow {
  id: string; license_id: string; summary: string; binary_version: string;
  occurrences: number; first_seen_at: string; last_seen_at: string; status: string;
}

export interface PlatformBugDetailRow extends PlatformBugRow {
  installation_id: string | null;
  content_hash: string;
  full_context: string | null;
  fixed_in_version: string | null;
  assigned_to: string | null;
  triage_notes: string | null;
}

export type TriageStatus = 'new' | 'investigating' | 'fixed' | 'wontfix' | 'verified';

// ── Customer tickets ─────────────────────────────────────────────
export type TicketStatus = 'open' | 'awaiting_customer' | 'awaiting_staff' | 'resolved' | 'closed';
export type TicketCategory = 'support' | 'billing' | 'feature_request' | 'bug_report' | 'other';
export type TicketPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface CustomerTicketRow {
  id: string;
  customer_id: string;
  license_id: string | null;
  subject: string;
  body: string;
  category: TicketCategory;
  priority: TicketPriority;
  status: TicketStatus;
  assigned_to: string | null;
  last_activity_at: string;
  created_at: string;
  resolved_at: string | null;
  closed_at: string | null;
}

export interface CustomerTicketReplyRow {
  id: string;
  ticket_id: string;
  author_type: 'customer' | 'staff';
  author_id: string;
  body: string;
  internal: boolean;
  created_at: string;
}

export interface CustomerCertRow {
  id: string;
  license_id: string;
  installation_id: string | null;
  kid: string;
  status: 'active' | 'rotated' | 'revoked';
  issued_at: string;
  expires_at: string;
  revoked_at: string | null;
  revoked_reason: string | null;
  signed_by_kid: string;
}

export type ReleaseType = 'CRITICAL' | 'PATCH' | 'FEATURE' | 'ENGINE';

export interface NextReleaseSuggestion {
  last: {
    version: string;
    image: string;
    type: ReleaseType | null;
    severity: 'security' | 'recommended' | 'optional';
    available_from_tier: 'starter' | 'business' | 'enterprise';
    released_at: string;
  } | null;
  suggested_version: string;
  image_template: string;
  defaults: {
    severity: 'security' | 'recommended' | 'optional';
    type: ReleaseType;
    available_from_tier: 'starter' | 'business' | 'enterprise';
  };
  placeholders: {
    image_digest: string;
    binary_hash: string;
    manifest_signature: string;
    note: string;
  } | null;
}

export interface UpdateReleaseRow {
  version: string;
  image: string;
  image_digest: string;
  binary_hash: string;
  released_at: string;
  min_current_version: string | null;
  severity: 'security' | 'recommended' | 'optional';
  /** Release category (Release Management page). Null on legacy rows. */
  type: ReleaseType | null;
  release_notes_url: string | null;
  manifest_signature: string;
  available_from_tier: 'starter' | 'business' | 'enterprise';
  is_yanked: boolean;
  yanked_reason: string | null;
  /** Staged rollout fraction 0-100. 100 = fully released. */
  rollout_percent: number;
  /** Phase A (2026-05-14): channel — 'internal' | 'alpha' | 'beta' | 'rc' | 'stable'.
   *  Backfilled from the version-suffix on migration 032; mutable via promote. */
  channel: 'internal' | 'alpha' | 'beta' | 'rc' | 'stable' | string;
  /** Phase A: mandatory flag — engine force-applies past deadline (Phase E). */
  mandatory: boolean;
  /** Phase A: who registered this release (nullable for backfilled rows). */
  published_by: string | null;
  /** Phase A: when the release moved between channels (null = never). */
  promoted_at: string | null;
  /** Phase A: prior channel; pairs with promoted_at. */
  promoted_from_channel: string | null;
  /** 2026-08-17: TRUE when registered by the CI pipeline
   *  (POST /ci/update-releases) but not yet classified + published by
   *  an operator. Draft rows are invisible to every customer engine;
   *  the Updates page shows them in a "Detected — classify & publish"
   *  lane. Undefined on rows fetched before the column existed. */
  is_draft?: boolean;
  /** 2026-08-18: source commit the build came from + its message
   *  (the message is the release description). */
  commit_sha?: string | null;
  commit_message?: string | null;
}

export interface ReleaseCandidate {
  configured: boolean;
  channel: string;
  current_version: string;
  latest_commit: { sha: string; message: string; author: string; date: string } | null;
  has_new_code: boolean;
  next: { major: string; minor: string; patch: string };
}
export interface MarketplaceEntitlementRow {
  id: string;
  provider: 'aws_marketplace' | 'gcp_marketplace';
  external_id: string;
  license_id: string | null;
  status: string;
  action_taken: string | null;
  error: string | null;
  received_at: string;
  processed_at: string | null;
  /**
   * Field-allowlisted view of the webhook body for the signed-in role. Never
   * the raw payload; may be empty, and an older server may omit it entirely.
   */
  payload_json?: Record<string, unknown> | null;
  /** The server withheld fields from `payload_json` (always true on current servers). */
  payload_redacted?: boolean;
  payload_view?: 'owner' | 'admin' | 'triage' | 'none';
  /** How many payload fields were withheld from this role. */
  payload_omitted_fields?: number;
}
export interface PlanPricingRow {
  plan_tier: 'starter' | 'business' | 'enterprise';
  base_price_cents: number;
  overage_price_cents: {
    max_accounts?: number;
    max_projects?: number;
    max_applications?: number;
  };
  updated_at: string;
}

// ── Sprint K: staff ──────────────────────────────────────────────────
export type StaffRole = 'owner' | 'admin' | 'support' | 'readonly';
export interface StaffRow {
  id: string;
  email: string;
  name: string;
  sso_provider: 'google' | 'okta';
  role: StaffRole;
  totp_enabled: boolean;
  last_login_at: string | null;
  disabled_at: string | null;
}

// ── Sprint L: notes + search ─────────────────────────────────────────
export interface CustomerNote {
  id: string;
  customer_id: string;
  author_id: string | null;
  author_email: string | null;
  body: string;
  created_at: string;
}
export interface SearchHitCustomer {
  id: string; company_name: string; primary_email: string; status: string;
}
export interface SearchHitLicense {
  id: string; customer_id: string; activation_key: string;
  activation_key_redacted?: boolean;
  plan_tier: string; status: string;
}
export interface SearchHitInstallation {
  id: string; license_id: string; machine_fingerprint: string;
  os_platform: string; status: string;
}
export interface SearchResults {
  q: string;
  customers:     SearchHitCustomer[];
  licenses:      SearchHitLicense[];
  installations: SearchHitInstallation[];
}

// ── Sprint J: analytics ──────────────────────────────────────────────
export type TimeseriesMetric =
  | 'trials_created' | 'installer_downloads' | 'activations'
  | 'paid_conversions' | 'mrr_cents';
export type DistributionMetric =
  | 'plan_tier' | 'license_status' | 'license_type'
  | 'os_platform' | 'residency_region';

export interface TimeseriesResponse {
  metric: TimeseriesMetric;
  interval: 'hour' | 'day' | 'week';
  since: string;
  until: string;
  points: Array<{ bucket: string; value: number }>;
}
export interface DistributionResponse {
  metric: DistributionMetric;
  slices: Array<{ label: string; value: number }>;
}

// ── Sprint G: funnel + installer downloads ───────────────────────────
export interface FunnelSummary {
  period: { since: string; until: string };
  trials_created: number;
  downloads: number;
  activations: number;
  paid_conversions: number;
  download_to_activation_pct: number | null;
  trial_to_paid_pct: number | null;
}
export interface InstallerDownloadRow {
  id: string;
  ip_address: string | null;
  user_agent: string | null;
  filename: string;
  optional_email_hint: string | null;
  downloaded_at: string;
}

// 0.6.0 — engine registration / pull APIs.
// 0.6.0d — extended metadata fields (hostname, public_ip, …).
export interface EngineTunnelUrlResponse {
  ok: boolean;
  /** Full URL including the plaintext token. Shown once, never retrievable. */
  url: string;
  token_prefix: string;
  /** Null when the URL never expires — see never_expires. */
  expires_at: string | null;
  /** True when only revocation can end this URL. */
  never_expires: boolean;
  /**
   * True when the server kept an encrypted copy, so Show URL can display it
   * again later. False means this really is the only sight of it.
   */
  recoverable: boolean;
  /** False when the engine has not dialled in yet — the URL will 503 until it does. */
  tunnel_connected: boolean;
}

/** One live public URL for an engine. Never carries the token itself. */
export interface EngineTunnelToken {
  id: string;
  token_prefix: string;
  label: string | null;
  created_at: string;
  expires_at: string | null;
  never_expires: boolean;
  last_used_at: string | null;
  use_count: number;
  /** False when the URL can no longer be produced, so hide Show URL. */
  revealable: boolean;
}

export interface EngineRow {
  machine_id:               string;
  install_id:               string;
  fingerprint:              string | null;
  engine_version:           string | null;
  public_url:               string | null;
  first_seen_at:            string;
  /** Registration refresh (about every 6 h). NOT a liveness signal: use `liveness` / `last_contact_at`. */
  last_seen_at:             string;
  /** Last authenticated request (the 60 s licence pull); null for engines that never authenticated. */
  last_auth_at?:            string | null;
  /** VF-35: freshest contact (pull, registration, heartbeat) and the server's verdict on it. */
  last_contact_at?:         string | null;
  contact_age_seconds?:     number | null;
  liveness?:                Liveness;
  hostname:                 string | null;
  public_ip:                string | null;
  fqdn:                     string | null;
  region:                   string | null;
  provider:                 string | null;
  node_name:                string | null;
  operator_email:           string | null;
  os_distro:                string | null;
  os_version:               string | null;
  kernel:                   string | null;
  bound_license_id:         string | null;
  bound_license_status:     string | null;
  bound_license_tier:       string | null;
  bound_license_type:       string | null;
  bound_license_expires_at: string | null;
  /** True while the engine is holding a live reverse tunnel to the relay. */
  tunnel_connected?:        boolean;
  /** Number of unexpired, unrevoked public-URL tokens. */
  tunnel_token_count?:      number;
  /**
   * Latest expiry among those tokens, so the UI can show when access lapses.
   * Null when there are no tokens — or when every one of them never expires,
   * which is why tunnel_never_expires exists rather than overloading this.
   */
  tunnel_expires_at?:       string | null;
  /** True when at least one live token for this engine has no expiry. */
  tunnel_never_expires?:    boolean;
  /** Engine identity key (protocol v2): the ACTIVE key's id, safe to display; null = not enrolled (legacy engine). */
  key_id?:                  string | null;
  key_created_at?:          string | null;
  /** Source address of the first registration. */
  first_seen_ip?:           string | null;
  /** The key staff saw when they issued / bound a licence. */
  adopted_key_id?:          string | null;
  /** Set when repeated request-counter regressions suggest the key is on two machines; licence delivery is stopped. */
  clone_suspected_at?:      string | null;
  last_seq?:                string;
}
export interface IssueEngineTrialBody {
  company_name?:  string;
  primary_email?: string;
  plan_tier?:     'starter' | 'business' | 'enterprise';
  trial_days?:    number;
  limits?:        Record<string, number>;
  features?:      string[];
}
export interface IssueEngineTrialResponse {
  ok:                 boolean;
  license_id:         string;
  customer_id:        string;
  machine_id_binding: string;
  activation_key:     string;
  expires_at:         string;
  trial_days:         number;
}

/**
 * Dispatched on any authenticated 401 from the server — lets the
 * SessionProvider clear its state and redirect to /signin without the
 * API client needing a reference to the React context.
 */
export const UNAUTHORIZED_EVENT = 'vloud:unauthorized';

function notifyUnauthorized(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
  }
}

// ── Contact messages (vloud.app "Contact us" form) ────────────────────
export type ContactStatus = 'new' | 'read' | 'replied';
export type ContactTopic = 'general' | 'sales' | 'support' | 'partnership' | 'migration' | 'other';

export interface ContactMessageRow {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  company: string | null;
  topic: ContactTopic;
  message: string;
  status: ContactStatus;
  source: string;
  created_at: string;
  read_at: string | null;
  replied_at: string | null;
  handled_by: string | null;
}

export interface ContactMessageList {
  messages: ContactMessageRow[];
  /** Rows matching the current filter (for paging). */
  total: number;
  /** Messages nobody has opened yet, whatever the filter. */
  new_count: number;
}

export interface ContactMessageQuery {
  status?: ContactStatus | 'all';
  q?: string;
  limit?: number;
  offset?: number;
}

function contactQuery(args: ContactMessageQuery): string {
  const q = new URLSearchParams();
  if (args.status && args.status !== 'all') q.set('status', args.status);
  if (args.q && args.q.trim()) q.set('q', args.q.trim());
  if (args.limit) q.set('limit', String(args.limit));
  if (args.offset) q.set('offset', String(args.offset));
  const text = q.toString();
  return text ? `?${text}` : '';
}


// ── Stripe connection (GET/PUT/DELETE /internal/settings/stripe ...) ─────────────────────────────────────────────────────
export type StripePlan = 'business' | 'enterprise';
export type StripeSource = 'admin' | 'env' | 'none';
export interface StripePriceReport {
  mode: 'test' | 'live' | null;
  resolved: Partial<Record<StripePlan, { priceId: string; via: 'env' | 'lookup_key'; unitAmount: number | null; interval: string | null }>>;
  problems: Array<{ plan: StripePlan; reason: string; detail: string }>;
}
export interface StripeStatus {
  configured: boolean;
  mode: 'test' | 'live' | null;
  source: StripeSource;
  key_prefix: string | null;
  key_tail: string | null;
  webhook_configured: boolean;
  webhook_source: StripeSource;
  webhook_tail: string | null;
  can_store: boolean;
  stored: { present: boolean; readable: boolean; updated_at: string | null; updated_by: string | null; account_id: string | null; account_name: string | null };
  env: { has_secret_key: boolean; has_webhook_secret: boolean };
  prices: StripePriceReport | null;
  webhook_url: string;
  /** The exact words the owner must type to put a live key in or create things in a live account. */
  live_confirmation: string;
  /** Licences that carry a Stripe subscription id (null when it could not be counted). */
  linked_subscriptions: number | null;
}
export interface StripeSetupPrice {
  plan: StripePlan; lookupKey: string; priceId: string | null; unitAmount: number | null;
  action: 'created' | 'exists' | 'exists_other_amount' | 'missing' | 'would_create';
}
export interface StripeWebhookState { url: string; id: string | null; action: 'created' | 'exists' | 'updated' | 'missing' | 'would_create'; secret_saved?: boolean }
export interface StripeVerifyResult {
  ok: boolean;
  mode: 'test' | 'live' | null;
  account?: { id: string | null; name: string | null; chargesEnabled: boolean | null; country: string | null };
  error?: string; message?: string;
  prices?: StripeSetupPrice[];
  webhook?: StripeWebhookState | null;
  webhook_url?: string;
}
export interface StripeSetupResult { ok: boolean; mode: 'test' | 'live' | null; prices: StripeSetupPrice[]; webhook: StripeWebhookState; status: StripeStatus }


export class AdminApiClient {
  constructor(private readonly opts: ApiClientOptions) {}

  private async req<T>(path: string): Promise<T> {
    const token = this.opts.getToken();
    if (!token) throw new ApiError(401, 'no session token', null);
    const f = this.opts.fetch ?? fetch;
    const res = await f(`${this.opts.baseUrl}${path}`, {
      headers: { 'authorization': `Bearer ${token}` },
    });
    const body = await res.json().catch(() => null) as ApiErrorBody | T | null;
    if (res.status === 401) notifyUnauthorized();
    if (!res.ok) throw new ApiError(res.status, `GET ${path} failed: ${res.status}`, body as ApiErrorBody | null);
    return body as T;
  }

  summary(): Promise<DashboardSummary> {
    return this.req<DashboardSummary>('/internal/dashboard/summary');
  }
  customers(limit = 100): Promise<{ customers: CustomerRow[] }> {
    return this.req(`/internal/customers?limit=${limit}`);
  }
  licenses(args: {
    status?: string; limit?: number;
    license_type?: string; expires_before?: string;
  } = {}): Promise<{ licenses: LicenseRow[] }> {
    const q = new URLSearchParams();
    if (args.status) q.set('status', args.status);
    if (args.limit)  q.set('limit',  String(args.limit));
    if (args.license_type)   q.set('license_type',   args.license_type);
    if (args.expires_before) q.set('expires_before', args.expires_before);
    return this.req(`/internal/licenses${q.toString() ? '?' + q : ''}`);
  }
  installations(args: { stale?: boolean; limit?: number } = {}): Promise<{ installations: InstallationRow[]; engines_without_installation?: EnginesWithoutInstallation }> {
    const q = new URLSearchParams();
    if (args.stale) q.set('stale', 'true');
    if (args.limit) q.set('limit', String(args.limit));
    return this.req(`/internal/installations${q.toString() ? '?' + q : ''}`);
  }
  auditLogs(args: { action?: string; limit?: number } = {}): Promise<{ audit_logs: AuditLogRow[] }> {
    const q = new URLSearchParams();
    if (args.action) q.set('action', args.action);
    if (args.limit)  q.set('limit',  String(args.limit));
    return this.req(`/internal/audit-logs${q.toString() ? '?' + q : ''}`);
  }
  licenseDetail(id: string): Promise<LicenseDetail> {
    return this.req<LicenseDetail>(`/internal/licenses/${id}`);
  }
  // ── Sprint G: funnel + installer-downloads ─────────────────────
  funnel(args: { since?: string; until?: string } = {}): Promise<FunnelSummary> {
    const q = new URLSearchParams();
    if (args.since) q.set('since', args.since);
    if (args.until) q.set('until', args.until);
    return this.req<FunnelSummary>(`/internal/funnel${q.toString() ? '?' + q : ''}`);
  }
  installerDownloads(args: { filename?: string; limit?: number } = {}): Promise<{ installer_downloads: InstallerDownloadRow[] }> {
    const q = new URLSearchParams();
    if (args.filename) q.set('filename', args.filename);
    if (args.limit)    q.set('limit',    String(args.limit));
    return this.req(`/internal/installer-downloads${q.toString() ? '?' + q : ''}`);
  }

  // 0.6.0 — Engine-aware admin APIs. Engines self-register on boot,
  // admins discover them here, and an admin issues a trial bound to
  // a specific machine_id. The engine's pull worker discovers the
  // license at next poll.
  engines(): Promise<{ engines: EngineRow[] }> {
    return this.req('/internal/engines');
  }
  issueEngineTrial(machineId: string, body: IssueEngineTrialBody): Promise<IssueEngineTrialResponse> {
    return this.post(`/internal/engines/${encodeURIComponent(machineId)}/issue-trial`, body);
  }
  /**
   * Mint the public URL for an engine's control panel.
   *
   * The `url` in the response contains the plaintext token and is the ONLY
   * time it is available — the server stores a hash. Show it, let the
   * operator copy it, and do not expect to read it back.
   */
  createEngineTunnelUrl(
    machineId: string,
    body?: { ttl_seconds?: number | null; label?: string },
  ): Promise<EngineTunnelUrlResponse> {
    return this.post(`/internal/engines/${encodeURIComponent(machineId)}/tunnel-token`, body ?? {});
  }
  /**
   * Reset an engine's tunnel so its client redials immediately.
   *
   * Only meaningful while the engine is connected -- it drops the relay side
   * of a live socket. An engine that is already offline answers 409, because
   * the cloud cannot reach it to ask.
   */
  reconnectEngineTunnel(machineId: string): Promise<{ ok: boolean; reconnecting: boolean }> {
    return this.post(`/internal/engines/${encodeURIComponent(machineId)}/tunnel/reconnect`, {});
  }

  /** The live public URLs for an engine. Returns no secrets. */
  listEngineTunnelTokens(machineId: string): Promise<{ tokens: EngineTunnelToken[] }> {
    return this.req(`/internal/engines/${encodeURIComponent(machineId)}/tunnel-tokens`);
  }

  /**
   * Re-display a URL that was already minted. Audited server-side, so do not
   * call it speculatively -- only when the operator asks.
   */
  revealEngineTunnelUrl(machineId: string, tokenId: string): Promise<{ ok: boolean; url: string }> {
    return this.post(
      `/internal/engines/${encodeURIComponent(machineId)}/tunnel-tokens/${encodeURIComponent(tokenId)}/reveal`,
      {},
    );
  }

  /** Revoke every live public URL for an engine. */
  revokeEngineTunnelUrls(machineId: string): Promise<{ ok: boolean; revoked: number }> {
    return this.delete(`/internal/engines/${encodeURIComponent(machineId)}/tunnel-token`);
  }
  /** Mint a one-time enrollment code (key rotation / server migration). The code is shown ONCE. */
  createEngineEnrollmentCode(machineId: string, body?: { ttl_hours?: number; any_machine?: boolean; reason?: string }): Promise<{ ok: boolean; enrollment_code: string; expires_at: string }> {
    return this.post(`/internal/engines/${encodeURIComponent(machineId)}/enrollment-code`, body ?? {});
  }
  /** Retire the engine's active identity key; the engine must re-enrol. */
  resetEngineKey(machineId: string, reason?: string): Promise<{ ok: boolean; retired_key_id: string | null }> {
    return this.post(`/internal/engines/${encodeURIComponent(machineId)}/reset-key`, reason ? { reason } : {});
  }
  /** Lift a clone quarantine; optionally retire the (possibly copied) key and mint a code for the real engine. */
  clearEngineQuarantine(machineId: string, body?: { reset_key?: boolean; issue_enrollment_code?: boolean; reason?: string }): Promise<{ ok: boolean; was_quarantined: boolean; retired_key_id: string | null; enrollment_code?: string }> {
    return this.post(`/internal/engines/${encodeURIComponent(machineId)}/clear-quarantine`, body ?? {});
  }
  bindLicenseToMachine(licenseId: string, machineId: string): Promise<{ ok: boolean; license_id: string; machine_id_binding: string }> {
    return this.post(`/internal/licenses/${licenseId}/bind-machine`, { machine_id: machineId });
  }
  deleteInstallerDownload(id: string): Promise<{ id: string; deleted: true }> {
    return this.delete(`/internal/installer-downloads/${id}`);
  }

  // ── Sprint H: admin billing actions ────────────────────────────
  sendCheckoutLink(customerId: string, body: {
    license_id: string;
    plan_tier?: 'starter' | 'business' | 'enterprise';
  }): Promise<{ customer_id: string; license_id: string; checkout_url: string; email_sent: boolean }> {
    return this.post(`/internal/customers/${customerId}/send-checkout-link`, body);
  }
  cancelSubscription(licenseId: string, body: {
    at_period_end: boolean; reason?: string;
  }): Promise<{ license_id: string; subscription_id: string; status: string; cancel_at_period_end: boolean }> {
    return this.post(`/internal/licenses/${licenseId}/cancel-subscription`, body);
  }
  refundLatestPayment(licenseId: string, body: {
    amount_cents?: number; reason: string;
  }): Promise<{ license_id: string; refund_id: string; amount_cents: number; status: string }> {
    return this.post(`/internal/licenses/${licenseId}/refund`, body);
  }
  changePlan(licenseId: string, body: {
    new_plan_tier: 'starter' | 'business' | 'enterprise';
  }): Promise<{ license_id: string; subscription_id: string; plan_tier: string }> {
    return this.post(`/internal/licenses/${licenseId}/change-plan`, body);
  }

  // ── Sprint I: machine lifecycle ───────────────────────────────
  deactivateInstallation(id: string, body: { reason?: string } = {}): Promise<{
    id: string; license_id: string; status: string;
  }> {
    return this.post(`/internal/installations/${id}/deactivate`, body);
  }
  updateMaxActivations(licenseId: string, body: {
    max_activations: number; reason?: string;
  }): Promise<{ id: string; max_activations: number; activation_count: number }> {
    return this.put(`/internal/licenses/${licenseId}/max-activations`, body);
  }

  /**
   * The two clocks that decide how fast a status change reaches an engine
   * and how fast it acts on one. Send null to clear an override and fall
   * back to the plan default; omit a field to leave it untouched.
   */
  /** Platform-wide timing defaults. A per-licence override always wins. */
  platformTiming(): Promise<{
    default_heartbeat_interval_seconds: number | null;
    default_revoke_grace_seconds: number | null;
    builtin_heartbeat_interval_seconds: number;
    builtin_revoke_grace_seconds: number;
  }> {
    return this.req('/internal/settings/timing');
  }

  updatePlatformTiming(body: {
    default_heartbeat_interval_seconds?: number | null;
    default_revoke_grace_seconds?: number | null;
    reason?: string;
  }): Promise<{
    default_heartbeat_interval_seconds: number | null;
    default_revoke_grace_seconds: number | null;
    note: string;
  }> {
    return this.put('/internal/settings/timing', body);
  }

  // ── Stripe connection (owner makes changes; owner/admin read) ──
  stripeSettings(): Promise<StripeStatus> {
    return this.req('/internal/settings/stripe');
  }
  /** Ask Stripe whether a key works and what its account already contains. Changes nothing. Omit the key to check the saved one. */
  verifyStripeKey(secretKey?: string): Promise<StripeVerifyResult> {
    return this.post('/internal/settings/stripe/verify', secretKey ? { secret_key: secretKey } : {});
  }
  saveStripeSettings(body: { secret_key?: string; webhook_secret?: string | null; confirm?: string; reason?: string }): Promise<{ ok: true; status: StripeStatus }> {
    return this.put('/internal/settings/stripe', body);
  }
  setupStripe(body: { amounts_usd?: { business?: number; enterprise?: number }; check_only?: boolean; confirm?: string }): Promise<StripeSetupResult> {
    return this.post('/internal/settings/stripe/setup', body);
  }
  refreshStripePrices(): Promise<{ ok: true; status: StripeStatus }> {
    return this.post('/internal/settings/stripe/refresh-prices', {});
  }
  clearStripeSettings(): Promise<{ ok: true; status: StripeStatus }> {
    return this.delete('/internal/settings/stripe');
  }

  updateTiming(licenseId: string, body: {
    heartbeat_interval_seconds?: number | null;
    revoke_grace_seconds?: number | null;
    reason?: string;
  }): Promise<{
    id: string;
    heartbeat_interval_seconds: number | null;
    revoke_grace_seconds: number | null;
    note: string;
  }> {
    return this.put(`/internal/licenses/${licenseId}/timing`, body);
  }

  // ── Sprint J: analytics ───────────────────────────────────────
  analyticsTimeseries(args: {
    metric: TimeseriesMetric;
    since?: string; until?: string;
    interval?: 'hour' | 'day' | 'week';
  }): Promise<TimeseriesResponse> {
    const q = new URLSearchParams({ metric: args.metric });
    if (args.since)    q.set('since', args.since);
    if (args.until)    q.set('until', args.until);
    if (args.interval) q.set('interval', args.interval);
    return this.req<TimeseriesResponse>(`/internal/analytics/timeseries?${q}`);
  }
  analyticsDistribution(metric: DistributionMetric): Promise<DistributionResponse> {
    return this.req<DistributionResponse>(
      `/internal/analytics/distribution?metric=${metric}`,
    );
  }

  // ── Sprint K: staff ──────────────────────────────────────────
  listStaff(includeDisabled = false): Promise<{ staff: StaffRow[] }> {
    return this.req(`/internal/staff${includeDisabled ? '?include_disabled=true' : ''}`);
  }
  createStaff(body: {
    email: string; name: string;
    sso_provider: 'google' | 'okta'; sso_subject_id: string;
    role: StaffRole;
  }): Promise<{ id: string }> {
    return this.post(`/internal/staff`, body);
  }
  updateStaff(id: string, body: { name?: string; role?: StaffRole }): Promise<{ id: string; updated: true }> {
    return this.patch(`/internal/staff/${id}`, body);
  }
  disableStaff(id: string): Promise<{ id: string; disabled: true }> {
    return this.patch(`/internal/staff/${id}/disable`, {});
  }
  enableStaff(id: string): Promise<{ id: string; enabled: true }> {
    return this.patch(`/internal/staff/${id}/enable`, {});
  }

  // Self-service profile (the TopBar profile dropdown).
  getMe(): Promise<StaffRow> {
    return this.req(`/internal/staff/me`);
  }
  updateMe(body: { name: string }): Promise<{ id: string; updated: true }> {
    return this.patch(`/internal/staff/me`, body);
  }
  /** Re-uses the existing /admin/password/change endpoint. */
  changePassword(body: { current_password: string; new_password: string }): Promise<{ ok: true }> {
    return this.post(`/admin/password/change`, body);
  }

  // ── Sprint L: notes + search ──────────────────────────────────
  listCustomerNotes(customerId: string): Promise<{ notes: CustomerNote[] }> {
    return this.req(`/internal/customers/${customerId}/notes`);
  }
  createCustomerNote(customerId: string, body: string): Promise<{ id: string; created_at: string }> {
    return this.post(`/internal/customers/${customerId}/notes`, { body });
  }
  deleteNote(id: string): Promise<{ id: string; deleted: true }> {
    return this.delete(`/internal/notes/${id}`);
  }
  search(q: string): Promise<SearchResults> {
    return this.req<SearchResults>(`/internal/search?q=${encodeURIComponent(q)}`);
  }

  // ── Sprint M: super-admin hard deletes (owner-only) ──────────
  deleteCustomer(id: string):                Promise<{ id: string; deleted: true }> { return this.delete(`/internal/customers/${id}`); }
  deleteLicense(id: string):                 Promise<{ id: string; deleted: true }> { return this.delete(`/internal/licenses/${id}`); }
  deleteInstallation(id: string):            Promise<{ id: string; deleted: true }> { return this.delete(`/internal/installations/${id}`); }
  deleteAnomaly(id: string):                 Promise<{ id: string; deleted: true }> { return this.delete(`/internal/anomalies/${id}`); }
  deletePlatformBug(id: string):             Promise<{ id: string; deleted: true }> { return this.delete(`/internal/platform-bugs/${id}`); }
  deleteMarketplaceEntitlement(id: string):  Promise<{ id: string; deleted: true }> { return this.delete(`/internal/marketplace-entitlements/${id}`); }
  anomalies(unresolvedOnly = false): Promise<{ anomalies: AnomalyRow[] }> {
    return this.req(`/internal/anomalies?unresolved_only=${unresolvedOnly}`);
  }
  platformBugs(): Promise<{ platform_bugs: PlatformBugRow[] }> {
    return this.req(`/internal/platform-bugs`);
  }
  platformBug(id: string): Promise<PlatformBugDetailRow> {
    return this.req(`/internal/platform-bugs/${id}`);
  }
  updatePlatformBug(id: string, body: {
    status?: TriageStatus;
    assigned_to?: string | null;
    triage_notes?: string | null;
    fixed_in_version?: string | null;
  }): Promise<PlatformBugDetailRow> {
    return this.put(`/internal/platform-bugs/${id}`, body);
  }
  // ── Contact messages (owner / admin only) ─────────────────────────
  contactMessages(args: ContactMessageQuery = {}): Promise<ContactMessageList> {
    return this.req(`/internal/contact-messages${contactQuery(args)}`);
  }
  contactMessagesSummary(): Promise<{ new_count: number }> {
    return this.req('/internal/contact-messages/summary');
  }
  setContactMessageStatus(id: string, status: ContactStatus): Promise<{ message: ContactMessageRow }> {
    return this.patch(`/internal/contact-messages/${id}`, { status });
  }
  deleteContactMessage(id: string): Promise<{ id: string; deleted: true }> {
    return this.delete(`/internal/contact-messages/${id}`);
  }
  /**
   * The CSV as a Blob. Not res.text(): that drops the UTF-8 byte-order mark, and
   * without it Excel garbles Arabic and accented names.
   */
  async contactMessagesCsv(args: Pick<ContactMessageQuery, 'status' | 'q'> = {}): Promise<{ blob: Blob; filename: string }> {
    const token = this.opts.getToken();
    if (!token) throw new ApiError(401, 'no session token', null);
    const f = this.opts.fetch ?? fetch;
    const path = `/internal/contact-messages/export${contactQuery(args)}`;
    const res = await f(`${this.opts.baseUrl}${path}`, { headers: { 'authorization': `Bearer ${token}` } });
    if (res.status === 401) notifyUnauthorized();
    if (!res.ok) {
      const body = await res.json().catch(() => null) as ApiErrorBody | null;
      throw new ApiError(res.status, `GET ${path} failed: ${res.status}`, body);
    }
    const filename = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'contact-messages.csv';
    return { blob: await res.blob(), filename };
  }

  updateReleases(): Promise<{ releases: UpdateReleaseRow[] }> {
    return this.req(`/internal/update-releases`);
  }
  createUpdateRelease(body: {
    version: string;
    image: string;
    image_digest: string;
    binary_hash: string;
    severity: 'security' | 'recommended' | 'optional';
    type?: ReleaseType | null;
    available_from_tier: 'starter' | 'business' | 'enterprise';
    manifest_signature: string;
    min_current_version?: string | null;
    release_notes_url?: string | null;
    rollout_percent?: number;
  }): Promise<UpdateReleaseRow> {
    return this.post(`/internal/update-releases`, body);
  }
  yankUpdateRelease(version: string, reason?: string): Promise<UpdateReleaseRow> {
    return this.put(`/internal/update-releases/${encodeURIComponent(version)}/yank`, { reason });
  }
  /**
   * 2026-08-17: classify + publish a CI-registered DRAFT release. The
   * draft already carries the immutable identity fields (version /
   * image / digests / signature) from the pipeline; this call sets the
   * operator-chosen classification + rollout and flips is_draft→FALSE,
   * making it visible to the fleet. 409 `not_a_draft` if already
   * published; 404 if the version doesn't exist.
   */
  /**
   * 2026-08-18: what the panel needs to offer "cut a release" — the
   * engine repo's latest main commit, the current version, and the
   * computed next-versions for major/minor/patch. 503 if the DevOps
   * trigger isn't configured server-side.
   */
  releaseCandidate(channel = 'beta'): Promise<ReleaseCandidate> {
    return this.req(`/internal/release-candidate?channel=${encodeURIComponent(channel)}`);
  }
  /**
   * 2026-08-18: cut a release — pick the semver bump + channel; the
   * server computes the version and queues the signed build pipeline.
   * The build registers the draft (commit message becomes the
   * description); the operator then sets rollout + publishes.
   */
  /**
   * Queue a build. The server returns the queued row's id -- NOT a run_url:
   * this stopped going through a hosted pipeline when it moved to the
   * self-hosted runner, and the old `run_id`/`run_url` fields were never
   * populated after that. Declaring them meant the UI rendered a dead link.
   */
  cutRelease(bump: 'major' | 'minor' | 'patch', channel = 'beta'): Promise<{
    version: string; channel: string; build_request_id: string | null;
  }> {
    return this.post(`/internal/update-releases/cut`, { bump, channel });
  }

  /** Status of a queued build, including the runner's error tail when it failed. */
  buildRequest(id: string): Promise<{
    id: string; version: string; channel: string; mode: string; bump: string | null;
    status: 'pending' | 'claimed' | 'running' | 'done' | 'failed';
    detail: string | null;
    created_at: string; claimed_at: string | null; finished_at: string | null;
  }> {
    return this.req(`/internal/build-requests/${encodeURIComponent(id)}`);
  }
  /**
   * 2026-08-18: discard a DRAFT (superseded / abandoned candidate).
   * Drafts only — a published release is yanked, not discarded. 409 if
   * the version is already published; 404 if it doesn't exist.
   */
  discardDraft(version: string): Promise<{ discarded: string }> {
    return this.delete(`/internal/update-releases/${encodeURIComponent(version)}`);
  }
  publishDraftRelease(version: string, body: {
    type: ReleaseType;
    severity: 'security' | 'recommended' | 'optional';
    available_from_tier: 'starter' | 'business' | 'enterprise';
    rollout_percent?: number;
    min_current_version?: string | null;
    release_notes_url?: string | null;
    mandatory?: boolean;
    channel?: 'internal' | 'alpha' | 'beta' | 'rc' | 'stable';
    /** default true — also flip the fresh-install download to this version. */
    promote_latest?: boolean;
  }): Promise<UpdateReleaseRow> {
    return this.put(`/internal/update-releases/${encodeURIComponent(version)}/publish`, body);
  }
  /**
   * Edit a subset of an existing release. Immutable identity fields
   * (version / image / image_digest / binary_hash / manifest_signature)
   * are not editable — staff publish a new version + yank to "fix" them.
   */
  editUpdateRelease(version: string, body: {
    severity?: 'security' | 'recommended' | 'optional';
    type?: ReleaseType | null;
    release_notes_url?: string | null;
    min_current_version?: string | null;
    available_from_tier?: 'starter' | 'business' | 'enterprise';
  }): Promise<UpdateReleaseRow> {
    return this.patch(`/internal/update-releases/${encodeURIComponent(version)}`, body);
  }
  setUpdateRolloutPercent(version: string, percent: number): Promise<UpdateReleaseRow> {
    return this.put(`/internal/update-releases/${encodeURIComponent(version)}/rollout`, {
      rollout_percent: Math.max(0, Math.min(100, Math.round(percent))),
    });
  }
  /**
   * Phase A (2026-05-14): toggle the mandatory flag on a release.
   * Engine surfaces a banner; Phase E will gate auto-apply past a deadline.
   */
  setUpdateMandatory(version: string, mandatory: boolean, reason?: string): Promise<UpdateReleaseRow> {
    return this.put(`/internal/update-releases/${encodeURIComponent(version)}/mandatory`, {
      mandatory, reason,
    });
  }
  /**
   * Phase I (2026-05-14): fleet adoption snapshot. Window in days
   * (default 7, clamped 1..90). Optional `version` returns a
   * `for_version` shortcut for the AdoptionBar component.
   */
  updateAdoption(opts: { window?: number; version?: string } = {}): Promise<{
    fleet_size: number;
    window_days: number;
    by_version: Array<{ version: string; count: number }>;
    for_version?: { version: string; count: number; pct: number };
  }> {
    const params = new URLSearchParams();
    if (opts.window) params.set('window', String(opts.window));
    if (opts.version) params.set('version', opts.version);
    const q = params.toString() ? `?${params.toString()}` : '';
    return this.req(`/internal/update-releases/adoption${q}`);
  }
  /**
   * Phase A: promote a release between channels. Validates server-side
   * against the canonical channel set (internal | alpha | beta | rc |
   * stable). Returns 409 `already_in_channel` when target == current.
   */
  promoteUpdateRelease(
    version: string,
    channel: 'internal' | 'alpha' | 'beta' | 'rc' | 'stable',
    reason?: string,
  ): Promise<UpdateReleaseRow> {
    return this.put(`/internal/update-releases/${encodeURIComponent(version)}/promote`, {
      channel, reason,
    });
  }
  /**
   * Wizard helper. Returns the most-recent published row, the bumped
   * suggested version, an image template, and (in dev) well-formed
   * placeholders for the three crypto fields.
   */
  nextReleaseSuggestion(): Promise<NextReleaseSuggestion> {
    return this.req(`/internal/update-releases/next-suggested`);
  }

  // ── Customer tickets ──────────────────────────────────────────
  customerTickets(status: TicketStatus | 'open_or_awaiting' = 'open_or_awaiting'): Promise<{ tickets: CustomerTicketRow[] }> {
    return this.req(`/internal/customer-tickets?status=${encodeURIComponent(status)}`);
  }
  customerTicket(id: string): Promise<{ ticket: CustomerTicketRow; replies: CustomerTicketReplyRow[] }> {
    return this.req(`/internal/customer-tickets/${encodeURIComponent(id)}`);
  }
  replyToCustomerTicket(id: string, body: { body: string; internal?: boolean; next_status?: TicketStatus }): Promise<CustomerTicketReplyRow> {
    return this.post(`/internal/customer-tickets/${encodeURIComponent(id)}/reply`, body);
  }
  triageCustomerTicket(id: string, body: {
    status?: TicketStatus;
    assigned_to?: string | null;
    priority?: TicketPriority;
  }): Promise<CustomerTicketRow> {
    return this.put(`/internal/customer-tickets/${encodeURIComponent(id)}`, body);
  }

  // ── Customer signing certs (G1) ───────────────────────────
  customerCerts(filter: { license_id?: string; status?: 'active' | 'rotated' | 'revoked' } = {}): Promise<{ certs: CustomerCertRow[] }> {
    const qs = new URLSearchParams();
    if (filter.license_id) qs.set('license_id', filter.license_id);
    if (filter.status) qs.set('status', filter.status);
    const suffix = qs.toString() ? `?${qs}` : '';
    return this.req(`/internal/customer-signing-certs${suffix}`);
  }
  revokeCustomerCert(kid: string, reason: string): Promise<{ kid: string; revoked: boolean }> {
    return this.delete(`/internal/customer-signing-certs/${encodeURIComponent(kid)}`, { reason });
  }

  getTicketSla(): Promise<TicketSlaSummary> {
    return this.req(`/internal/sla/tickets`);
  }

  // ── mutations ────────────────────────────────────────────────────────

  revokeLicense(id: string, reason?: string): Promise<{ id: string; status: string }> {
    return this.post(`/internal/licenses/${id}/revoke`, { reason });
  }
  suspendLicense(id: string, reason?: string): Promise<{ id: string; status: string }> {
    return this.post(`/internal/licenses/${id}/suspend`, { reason });
  }
  resumeLicense(id: string): Promise<{ id: string; status: string }> {
    return this.post(`/internal/licenses/${id}/resume`, {});
  }

  // ── V2 / locked-model — phase C.2 admin endpoints ────────────────────

  /**
   * Restore a suspended or past_due license. Equivalent to resume but
   * writes a locked-model `restored` event to license_lifecycle_events
   * with the staff-provided reason. Use this from the LicenseDetail
   * page's "Restore" action.
   */
  restoreLicense(id: string, reason?: string): Promise<{ id: string; status: string }> {
    return this.post(`/internal/licenses/${id}/restore`, { reason });
  }

  /**
   * Re-mint the license JWS without inserting usage. The customer's
   * engine picks up the new claims on its next heartbeat (≤ 6h) or
   * `vloud license refresh` CLI invocation.
   *
   * Used after editing limits / features / tier — gives the customer
   * an immediate "Push to engine now" path.
   */
  refreshLicense(id: string): Promise<{ id: string; rotated_secret: boolean }> {
    return this.post(`/internal/licenses/${id}/refresh`, {});
  }

  /**
   * Time-ranged capacity history for a license. Default window: last
   * 30 days. Used by LicenseDetail's "Capacity history" tab.
   */
  licenseCapacity(
    id: string,
    opts: { fromEpochSec?: number; toEpochSec?: number; limit?: number } = {},
  ): Promise<LicenseCapacityResponse> {
    const q = new URLSearchParams();
    if (opts.fromEpochSec) q.set('from', String(opts.fromEpochSec));
    if (opts.toEpochSec)   q.set('to',   String(opts.toEpochSec));
    if (opts.limit)        q.set('limit', String(opts.limit));
    const qs = q.toString();
    return this.req<LicenseCapacityResponse>(
      `/internal/licenses/${id}/capacity${qs ? '?' + qs : ''}`,
    );
  }

  /**
   * License lifecycle event timeline. Used by LifecycleTimeline.tsx.
   */
  licenseLifecycle(id: string, limit = 100): Promise<{ events: LifecycleEvent[] }> {
    return this.req<{ events: LifecycleEvent[] }>(
      `/internal/licenses/${id}/lifecycle?limit=${limit}`,
    );
  }

  /**
   * Installations bound to a license — most recent first. Used by
   * the LicenseDetail Heartbeats tab to show "who is currently
   * heartbeating" + drift across versions.
   */
  licenseInstallations(id: string): Promise<{
    license_id: string;
    installations: Array<{
      id: string;
      machine_fingerprint: string;
      os_distro: string | null;
      os_version: string | null;
      binary_version: string | null;
      status: string;
      activated_at: string;
      last_heartbeat_at: string | null;
    }>;
  }> {
    return this.req(`/internal/licenses/${id}/installations`);
  }
  issueLicense(body: {
    customer_id: string;
    plan_tier: 'starter' | 'business' | 'enterprise';
    license_type: 'monthly' | 'yearly' | 'lifetime' | 'offline';
    max_activations: number;
    features?: string[];
    /** Offline only: 1–365 days (ADR-014); the server defaults to 90. */
    validity_days?: number;
    /**
     * Explicit capacity. Omitted dimensions: a bound engine is signed with
     * 10 instances / 5 hosting accounts; key activation uses the tier defaults.
     */
    limits?: { max_instances?: number; max_linux_users?: number };
  }): Promise<{ id: string; activation_key: string; license_type?: string; expires_at?: string | null }> {
    return this.post(`/internal/licenses`, body);
  }
  createCustomer(body: {
    company_name: string; primary_email: string; country?: string;
  }): Promise<{ id: string }> {
    return this.post(`/internal/customers`, body);
  }

  listPricing(): Promise<{ plan_pricing: PlanPricingRow[] }> {
    return this.req(`/internal/pricing`);
  }

  listMarketplaceEntitlements(args: {
    provider?: 'aws_marketplace' | 'gcp_marketplace';
    status?: 'pending' | 'processed' | 'error';
    limit?: number;
  } = {}): Promise<{ marketplace_entitlements: MarketplaceEntitlementRow[] }> {
    const q = new URLSearchParams();
    if (args.provider) q.set('provider', args.provider);
    if (args.status) q.set('status', args.status);
    if (args.limit) q.set('limit', String(args.limit));
    return this.req(`/internal/marketplace-entitlements${q.toString() ? '?' + q : ''}`);
  }
  approveUnknownMarketplaceProduct(id: string, body: {
    plan_tier: 'starter' | 'business' | 'enterprise';
    max_activations: number;
    features: string[];
    overage_price_cents?: Record<string, number> | null;
  }): Promise<{ entitlement_id: string; product_code: string; mapping_updated: boolean }> {
    return this.post(`/internal/marketplace-entitlements/${id}/approve-unknown-product`, body);
  }
  updatePricing(planTier: 'starter' | 'business' | 'enterprise', body: {
    base_price_cents: number;
    overage_price_cents: { max_accounts?: number; max_projects?: number; max_applications?: number };
  }): Promise<{ plan_tier: string; updated: boolean }> {
    return this.put(`/internal/pricing/${planTier}`, body);
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    const token = this.opts.getToken();
    if (!token) throw new ApiError(401, 'no session token', null);
    const f = this.opts.fetch ?? fetch;
    const res = await f(`${this.opts.baseUrl}${path}`, {
      method: 'POST',
      headers: {
        'authorization': `Bearer ${token}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    const resBody = await res.json().catch(() => null) as ApiErrorBody | T | null;
    if (res.status === 401) notifyUnauthorized();
    if (!res.ok) throw new ApiError(res.status, `POST ${path} failed: ${res.status}`, resBody as ApiErrorBody | null);
    return resBody as T;
  }

  private async put<T>(path: string, body: unknown): Promise<T> {
    const token = this.opts.getToken();
    if (!token) throw new ApiError(401, 'no session token', null);
    const f = this.opts.fetch ?? fetch;
    const res = await f(`${this.opts.baseUrl}${path}`, {
      method: 'PUT',
      headers: { 'authorization': `Bearer ${token}`, 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    const resBody = await res.json().catch(() => null) as ApiErrorBody | T | null;
    if (res.status === 401) notifyUnauthorized();
    if (!res.ok) throw new ApiError(res.status, `PUT ${path} failed: ${res.status}`, resBody as ApiErrorBody | null);
    return resBody as T;
  }

  private async patch<T>(path: string, body: unknown): Promise<T> {
    const token = this.opts.getToken();
    if (!token) throw new ApiError(401, 'no session token', null);
    const f = this.opts.fetch ?? fetch;
    const res = await f(`${this.opts.baseUrl}${path}`, {
      method: 'PATCH',
      headers: { 'authorization': `Bearer ${token}`, 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    const resBody = await res.json().catch(() => null) as ApiErrorBody | T | null;
    if (res.status === 401) notifyUnauthorized();
    if (!res.ok) throw new ApiError(res.status, `PATCH ${path} failed: ${res.status}`, resBody as ApiErrorBody | null);
    return resBody as T;
  }

  private async delete<T>(path: string, body?: unknown): Promise<T> {
    const token = this.opts.getToken();
    if (!token) throw new ApiError(401, 'no session token', null);
    const f = this.opts.fetch ?? fetch;
    const headers: Record<string, string> = { 'authorization': `Bearer ${token}` };
    let payload: BodyInit | undefined;
    if (body !== undefined) {
      headers['content-type'] = 'application/json';
      payload = JSON.stringify(body);
    }
    const res = await f(`${this.opts.baseUrl}${path}`, {
      method: 'DELETE',
      headers,
      body: payload,
    });
    const resBody = await res.json().catch(() => null) as ApiErrorBody | T | null;
    if (res.status === 401) notifyUnauthorized();
    if (!res.ok) throw new ApiError(res.status, `DELETE ${path} failed: ${res.status}`, resBody as ApiErrorBody | null);
    return resBody as T;
  }
}
